import store from 'source/store';

function checkIfUserIsOrgExec() {
    const { is_organizational_exec } = store.state.auth.user.user_profile;

    return is_organizational_exec;
}

function hasPermission(workforce_id, permission) {
    let recruitmentBoardPermissions = [];
    const allWorkforcePermissions = store.state.auth.user.user_profile.recruitment_board_permissions;

    // Revalidates the Recruitment Board Permissions
    const hasWorkforcePermissions = allWorkforcePermissions.hasOwnProperty(workforce_id);
    const hasRecruitmentBoardPermission = (
        hasWorkforcePermissions && allWorkforcePermissions[workforce_id].includes(permission));

    // Updates the Permissions if it exists
    if (hasWorkforcePermissions)
        recruitmentBoardPermissions = allWorkforcePermissions[workforce_id];

    // This needs to be updated again because when the page refreshes,
    // `setUser` will always use the first Recruitment Board permission if it exists.
    // This ensures were using the proper Workforce Recruitment Permissions
    store.commit('auth/setUserWorkforceRecruitmentBoardPermissions', recruitmentBoardPermissions);

    return hasRecruitmentBoardPermission;
}

function checkJobOfferRequestPageAccess() {
    return (to, from, next) => {
        const { is_organizational_exec } = store.state.auth.user.user_profile;
        if (is_organizational_exec) {
            return next({ name: 'access-denied', replace: true });
        }
        const navigateToJobPosting = sessionStorage.getItem('navigate-to-job-posting');

        if (navigateToJobPosting) {
            sessionStorage.removeItem('navigate-to-job-posting');
            // Reset stepper state
            store.dispatch('jobOfferRequestsSteps/resetStepperState');
            store.commit('jobOffer/resetJobOfferRequestDetails');
            return next({
                replace: true,
                name: 'recruitment-posting',
                params: to.params,
            });
        }
        next();

    };
}

function checkJobOfferPageAccess() {
    return (to, from, next) => {
        const { is_organizational_exec } = store.state.auth.user.user_profile;
        const canSendJobOffer = store.state.auth.userWorkforceRecruitmentBoardPermissions?.includes('add_joboffer');

        if (!is_organizational_exec && !canSendJobOffer) {
            return next({ name: 'access-denied', replace: true });
        }

        const navigateToJobPosting = sessionStorage.getItem('navigate-to-job-posting');

        if (navigateToJobPosting) {
            sessionStorage.removeItem('navigate-to-job-posting');
            // Reset stepper state and job offer details in Vuex store
            store.dispatch('steps/resetStepperState');
            store.commit('jobOffer/resetJobOfferDetails');
            return next({
                replace: true,
                name: 'recruitment-posting',
                params: to.params,
            });
        }

        next();

    };
}

function isUnauthorizedUser(workforce_id, requiredPermission) {
    return !checkIfUserIsOrgExec() && !hasPermission(workforce_id, requiredPermission);
}

function checkUserAccess(requiredPermission) {
    return (to, from, next) => {
        let workforce_id = to.params.workforce_id;
        if (isUnauthorizedUser(workforce_id, requiredPermission))
            next({ name: 'access-denied', replace: true });
        else next();
    };
}

function canAccessRecruitmentBoard() {
    return (to, from, next) => {
        let workforce_id = to.params.workforce_id;

        if (isUnauthorizedUser(workforce_id, 'can_view_recruitment_board'))
            next({ name: 'access-denied', replace: true });
        else next();
    };
}

function handlePermissionPageAccess() {
    return (to, from, next) => {
        if (isUnauthorizedUser('view_recruitmentboardaccess')) {
            next({ name: 'access-denied', replace: true });
        } else if (to.name === 'recruitment-permissions') {
            next({
                ...to,
                name: 'permission-recruitment',
                replace: true
            });
        } else {
            next();
        }
    };
}

export default [{
    path: 'recruitment',
    name: 'recruitment',
    component: () =>
        import('source/pages/root/RecruitmentPageGroup.vue'),
    // Checks user perms to view the recruitment board
    beforeEnter: canAccessRecruitmentBoard(),
    props: true,
    meta: {
        title: 'Recruitment',
        canDisplayGlobalModals: true
    },
    children: [{
        path: 'posting/:job_posting_pk',
        name: 'recruitment-posting',
        component: () =>
            import('source/pages/recruitment/RecruitmentPage.vue'),
        props: true,
        meta: {
            title: 'Job Posting - HQZen',
            canDisplayGlobalModals: true,
            isBoardRoute: true
        }
    }, {
        path: 'create-draft-job-posting/:draft_job_posting_pk',
        name: 'recruitment-create-draft-job-posting',
        component: () =>
            import(
                'source/components/employers/job-postings/CreateDraftJobPostingPage.vue'
            ),
        // Checks user perms to create draft job posting
        beforeEnter: checkUserAccess('add_jobposting'),
        props: true,
        meta: {
            title: 'Draft Job Posting - HQZen',
            canDisplayGlobalModals: true,
            isAdministratorRoute: true,
        }
    }, {
        path: 'job-offer/:job_offer_id',
        name: 'recruitment-job-offer',
        component: () =>
            import('source/pages/recruitment/JobOfferDetailsPage.vue'),
        props: true,
        meta: {
            title: 'Job Offer - HQZen',
            canDisplayGlobalModals: true
        }
    }, {
        // TODO: Improve route path and name to accommodate create/edit job offer
        path: 'send-job-offer/:job_posting_pk/career-setup',
        name: 'recruitment-set-up-send-new-job-offer',
        component: () => import ('source/pages/recruitment/JobOfferCreatePage.vue'),
        props: true,
        beforeEnter: checkJobOfferPageAccess(),
        meta: {
            title: 'Send Job Offer - HQZen',
            canDisplayGlobalModals: false
        }
    }, {
        path: 'send-job-offer-request/:job_posting_pk/career-setup',
        name: 'recruitment-set-up-send-job-offer-request',
        component: () => import ('source/pages/recruitment/JobOfferCreatePage.vue'),
        props: route => ({
            isJobOfferRequest: true
        }),
        beforeEnter: checkJobOfferRequestPageAccess(),
        meta: {
            title: 'Send Job Offer Request - HQZen',
            canDisplayGlobalModals: false
        }
    },
    {
        path: 'review-documents',
        name: 'recruitment-contracts',
        component: () => import('source/pages/recruitment/ContractReviewPage.vue'),
        // Check user perms to view contracts
        beforeEnter: checkUserAccess('change_joboffer'),
        props: true,
        meta: {
            title: 'Contracts - HQZen',
            canDisplayGlobalModals: false
        }
    }, {
        path: 'chats',
        name: 'recruitment-chats',
        component: () => import('source/pages/recruitment/ChatsPage.vue'),
        // Check user perms to view chats
        beforeEnter: checkUserAccess('view_joboffernegotiation'),
        props: true,
        meta: {
            title: 'Chats - HQZen',
            canDisplayGlobalModals: false
        }
    }, {
        path: 'permissions',
        name: 'recruitment-permissions',
        component: () => import('source/pages/recruitment/RecruitmentPermissionsPage.vue'),
        // Check user perms to view permissions
        beforeEnter: handlePermissionPageAccess(),
        props: true,
        meta: {
            title: 'Permissions - HQZen',
            canDisplayGlobalModals: false
        },
        children: [
            {
                path: 'recruitment-access',
                name: 'permission-recruitment',
                component: () => import('source/pages/recruitment/RecruitmentLevelPermissionSubpage.vue'),
                meta: {
                    title: 'Recruitment Permission - HQZen',
                    canDisplayGlobalModals: false
                }
            },
            {
                path: 'job-posting-access',
                name: 'permission-job-posting',
                component: () => import('source/pages/recruitment/JobPostingLevelPermissionSubpage.vue'),
                meta: {
                    title: 'Job Posting Permission - HQZen',
                    canDisplayGlobalModals: false
                }
            }
        ]
    }, {
        path: 'review-documents/:job_offer_id/contract-details',
        name: 'contract-details',
        component: () => import('source/pages/recruitment/ContractDetailsPage.vue'),
        // Check user perms to view contracts for a particular job offer
        beforeEnter: checkUserAccess('change_joboffer'),
        props: true,
        meta: {
            title: 'Contracts Details - HQZen',
            canDisplayGlobalModals: false,
        }
    }]
}];
